import React, { useEffect, useState, useRef } from 'react';
import { ArrowLeft, Plus, Search, Users, Info, LogOut, Trash2, Send, X, ShieldCheck, Image as ImageIcon, Mic, Video, Flag, UserMinus, Crown, Square, Languages, Download, Play, Pause, BarChart3, Lock } from 'lucide-react';
import { PollCard, CreatePollModal } from './GroupPolls';
import VoicePreviewBar from './VoicePreviewBar';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { getMediaUrl } from '../utils/mediaUrl';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { formatDayLabel } from '../utils/dateLabel';
import MessageContent from './MessageContent';
import MediaLightbox from './MediaLightbox';
import { renderMentions } from '../utils/mentions';
import GptAfricaCard, { africaTexts } from './GptAfricaCard';
const useLanguageSafe = () => { try { return localStorage.getItem('destine_language') || 'fr'; } catch { return 'fr'; } };

/**
 * Self-contained Groups panel — Phase 1.
 * Lists my groups, search any group, create new, request to join,
 * open group chat, view info modal, leave / delete group.
 */
const GroupsPanel = ({ currentUserId, onActiveGroupChange }) => {
  const { t, language } = useLanguage();
  const [myGroups, setMyGroups] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [infoGroup, setInfoGroup] = useState(null);

  // Notify parent (Messages.jsx) so it can hide its tabs / bottom Navigation
  // when the user enters a group chat (true full-screen WhatsApp-like UX).
  useEffect(() => {
    onActiveGroupChange?.(!!activeGroup);
  }, [activeGroup, onActiveGroupChange]);

  const loadMyGroups = async () => {
    try {
      const res = await axios.get('/api/groups/me', { params: { lang: language } });
      setMyGroups(res.data || []);
    } catch (e) {}
  };

  useEffect(() => { loadMyGroups(); }, [language]); // eslint-disable-line

  // When the user changes the app language, refresh the cached activeGroup
  // so the localized header/theme reflects immediately.
  useEffect(() => {
    if (!activeGroup?.id) return;
    axios.get(`/api/groups/${activeGroup.id}`, { params: { lang: language } })
      .then(r => { if (r.data) setActiveGroup(r.data); })
      .catch(() => {});
    // eslint-disable-next-line
  }, [language]);

  useEffect(() => {
    const q = searchQ.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`/api/groups/search?q=${encodeURIComponent(q)}`);
        setSearchResults(res.data || []);
      } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ]);

  if (activeGroup) {
    return (
      <>
        <GroupChatView
          group={activeGroup}
          currentUserId={currentUserId}
          onBack={() => { setActiveGroup(null); loadMyGroups(); }}
          onOpenInfo={() => setInfoGroup(activeGroup)}
        />
        {infoGroup && (
          <GroupInfoModal
            group={infoGroup}
            currentUserId={currentUserId}
            onClose={() => { setInfoGroup(null); loadMyGroups(); }}
            onLeave={() => { setInfoGroup(null); setActiveGroup(null); loadMyGroups(); }}
          />
        )}
      </>
    );
  }

  return (
    <div className="space-y-4" data-testid="groups-panel">
      {/* Search + Create row */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            placeholder={t('groups.search_groups') || 'Rechercher des groupes…'}
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-violet-500"
            data-testid="groups-search-input"
          />
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-3 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold flex items-center gap-1"
          data-testid="create-group-btn"
        >
          <Plus className="w-4 h-4" /> {t('groups.create') || 'Créer un groupe'}
        </button>
      </div>

      {/* Search results */}
      {searchQ.trim().length >= 2 && (
        <div className="space-y-1">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Résultats</p>
          {searchResults.length === 0 ? (
            <p className="text-sm text-gray-500 italic">{t('groups.no_groups') || 'Aucun groupe'}</p>
          ) : (
            searchResults.map(g => (
              <GroupCard key={g.id} group={g} onOpen={() => g.is_member ? setActiveGroup(g) : setInfoGroup(g)} />
            ))
          )}
        </div>
      )}

      {/* My groups */}
      <div className="space-y-1">
        <p className="text-xs text-gray-400 uppercase tracking-wide">{t('groups.tab') || 'Groupes'} ({myGroups.length})</p>
        {/* v50.10.8c — GPT Africa umbrella (country sub-groups), just below the Guyane training group */}
        {myGroups.filter(g => g.id === SYSTEM_GROUP_ID_REGIONAL).map(g => (
          <GroupCard key={g.id} group={g} onOpen={() => setActiveGroup(g)} />
        ))}
        <GptAfricaCard onJoined={loadMyGroups} />
        {myGroups.length === 0 ? (
          <p className="text-sm text-gray-500 italic py-6 text-center">{t('groups.no_groups') || 'Aucun groupe pour le moment'}</p>
        ) : (
          myGroups.filter(g => g.id !== SYSTEM_GROUP_ID_REGIONAL).map(g => (
            <GroupCard key={g.id} group={g} onOpen={() => setActiveGroup(g)} />
          ))
        )}
      </div>

      {showCreate && <CreateGroupModal onClose={() => setShowCreate(false)} onCreated={(g) => { setShowCreate(false); loadMyGroups(); setActiveGroup(g); }} />}
      {infoGroup && (
        <GroupInfoModal
          group={infoGroup}
          currentUserId={currentUserId}
          onClose={() => { setInfoGroup(null); loadMyGroups(); }}
          onLeave={() => { setInfoGroup(null); setActiveGroup(null); loadMyGroups(); }}
        />
      )}
    </div>
  );
};

const GroupCard = ({ group, onOpen }) => (
  <button
    onClick={onOpen}
    className="w-full flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-xl text-left transition-colors"
    data-testid={`group-card-${group.id}`}
  >
    <div className="w-12 h-12 rounded-full bg-violet-700/30 overflow-hidden flex items-center justify-center shrink-0">
      {group.profile_image_url ? (
        <img src={getMediaUrl(group.profile_image_url)} alt={group.name} className="w-full h-full object-cover" />
      ) : (
        <Users className="w-6 h-6 text-violet-300" />
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold truncate">{group.name}</p>
      <p className="text-xs text-gray-400 truncate">{group.theme}</p>
    </div>
    <div className="text-[10px] text-gray-500">{group.members_count || 0}</div>
  </button>
);

const CreateGroupModal = ({ onClose, onCreated }) => {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [theme, setTheme] = useState('');
  const [rules, setRules] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const fileInputRef = useRef(null);

  // Lock body scroll while modal is open + listen to Capacitor keyboard events
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    let listeners = [];
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
          const { Keyboard } = await import('@capacitor/keyboard');
          const show = await Keyboard.addListener('keyboardWillShow', (info) => {
            setKeyboardHeight(info?.keyboardHeight || 280);
          });
          const hide = await Keyboard.addListener('keyboardWillHide', () => {
            setKeyboardHeight(0);
          });
          listeners = [show, hide];
        }
      } catch (e) { /* web */ }
    })();
    return () => {
      document.body.style.overflow = prevOverflow;
      listeners.forEach(l => l?.remove?.());
    };
  }, []);

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post('/api/groups/upload-image', fd);
      setProfileImageUrl(res.data.url || '');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
  };

  const submit = async () => {
    if (!name.trim() || !theme.trim()) {
      toast.error('Name & theme required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post('/api/groups', {
        name: name.trim(),
        theme: theme.trim(),
        rules: rules.trim(),
        profile_image_url: profileImageUrl || null,
      });
      onCreated(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[55] bg-black/80 flex items-end sm:items-center justify-center"
      data-testid="create-group-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-zinc-900 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md border border-white/10 flex flex-col overflow-hidden"
        style={{
          maxHeight: keyboardHeight > 0
            ? `calc(100dvh - ${keyboardHeight}px - 8px)`
            : '92dvh',
          paddingTop: 'env(safe-area-inset-top, 0)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
          <h3 className="text-lg font-semibold">{t('groups.create') || 'Créer un groupe'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-full"><X className="w-5 h-5" /></button>
        </div>

        <div
          className="flex-1 overflow-y-auto p-4 space-y-4 overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className="flex flex-col items-center gap-2">
            <button onClick={() => fileInputRef.current?.click()} className="w-20 h-20 rounded-full bg-violet-700/30 flex items-center justify-center overflow-hidden border-2 border-violet-500/50">
              {profileImageUrl ? <img src={getMediaUrl(profileImageUrl)} alt="" className="w-full h-full object-cover" /> : <Users className="w-8 h-8 text-violet-300" />}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImagePick} className="hidden" data-testid="group-image-input" />
            <p className="text-xs text-gray-400">{t('groups.profile_image') || 'Photo de profil'}</p>
          </div>
          <Field label={t('groups.name') || 'Nom du groupe'} value={name} onChange={setName} testId="group-name-input" />
          <Field label={t('groups.theme') || 'Thématique'} value={theme} onChange={setTheme} testId="group-theme-input" />
          <Field label={t('groups.rules') || 'Règles'} value={rules} onChange={setRules} multiline testId="group-rules-input" />
        </div>

        <div
          className="p-4 border-t border-white/10 shrink-0"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <button
            onClick={submit}
            disabled={submitting}
            className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-50 font-semibold"
            data-testid="submit-create-group-btn"
          >
            {submitting ? '…' : (t('groups.create') || 'Créer')}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, multiline, testId }) => (  <div className="space-y-1">
    <label className="block text-xs text-gray-400">{label}</label>
    {multiline ? (
      <textarea value={value} onChange={e => onChange(e.target.value)} rows={3} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-500" data-testid={testId} />
    ) : (
      <input value={value} onChange={e => onChange(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-500" data-testid={testId} />
    )}
  </div>
);

const SYSTEM_GROUP_ID_REGIONAL = 'dsia-guyane-regional-v1';

/** Banner shown inside the regional-languages training group.
 *  Explains the contribution format (in three source languages) and lets
 *  the user pick which regional language they're currently translating to. */
const RegionalLangBanner = ({ regionalLang, setRegionalLang, languages, t, info, africaGroupId }) => {
  const leaveGroup = async () => {
    if (!window.confirm(t('regional.confirmLeave', 'Quitter le groupe ? Tu pourras le rejoindre à tout moment.'))) return;
    try {
      if (africaGroupId) await axios.post('/api/gpt-africa/leave', { group_id: africaGroupId });
      else await axios.post('/api/regional-lang/leave');
      toast.success(t('regional.left', 'Tu as quitté le groupe'));
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };
  const selectedLabel = (languages || []).find(l => l.id === regionalLang)?.label || regionalLang;
  return (
    <div
      className="px-4 py-3 border-b border-emerald-500/30 shrink-0"
      style={{
        backgroundImage: 'linear-gradient(135deg, rgba(16,185,129,0.18) 0%, rgba(251,191,36,0.12) 50%, rgba(244,63,94,0.12) 100%)',
      }}
      data-testid="regional-lang-banner"
    >
      {/* Heading row */}
      <div className="flex items-start gap-3 mb-2">
        <div className="shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-500 to-amber-500 flex items-center justify-center text-xl shadow-lg shadow-emerald-500/30">
          {africaGroupId ? '🌍' : '🇬🇫'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-bold text-white leading-tight">
            {africaGroupId ? africaTexts(useLanguageSafe()).title : t('regional.helpTitle', "Aide Destine IA à comprendre les langues régionales de Guyane.")}
          </p>
          <p className="text-[11px] text-emerald-100/80 mt-0.5 leading-snug">
            <span className="font-semibold text-emerald-200">{t('regional.activeLang', 'Langue active :')}</span>{' '}
            <span className="text-amber-200">{selectedLabel}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={leaveGroup}
          className="shrink-0 px-3 py-1.5 rounded-full text-[10px] font-semibold bg-white/10 hover:bg-rose-500/30 transition-colors"
          data-testid="regional-lang-leave-btn"
          title={t('regional.leave', 'Quitter ce groupe')}
        >
          {t('regional.leave', 'Quitter')}
        </button>
      </div>

      {/* Format helper */}
      <div className="rounded-xl bg-black/30 border border-emerald-500/20 px-3 py-2 mb-2">
        <p className="text-[10px] uppercase tracking-wider text-emerald-300/80 font-bold mb-1">
          {t('regional.formatTitle', 'Format')}
        </p>
        <p className="text-[11px] text-white/90 leading-snug">
          <span className="text-emerald-200 font-semibold">FR / EN / NL</span>
          {' '}<span className="text-amber-200 font-mono">=</span>{' '}
          <span className="text-amber-200 font-semibold">{selectedLabel}</span>
        </p>
        <p className="text-[10px] text-gray-400 italic mt-1 leading-snug">
          {t('regional.example', "Ex : Peux-tu me faire l'image d'une voiture rouge ? = Yu sa méké wang photo fou wang lébi wagi gim ?")}
        </p>
      </div>

      {/* Language picker */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-emerald-200/80 font-semibold uppercase tracking-wider shrink-0">
          {t('regional.pickerLabel', 'Langue :')}
        </span>
        <select
          value={regionalLang}
          onChange={e => setRegionalLang(e.target.value)}
          className="flex-1 bg-black/40 border border-emerald-500/40 rounded-full px-3 py-1.5 text-[11px] font-semibold focus:outline-none focus:border-emerald-400"
          data-testid="regional-lang-select"
        >
          {(languages || []).map(l => (
            <option key={l.id} value={l.id} className="bg-gray-900">{l.label}</option>
          ))}
        </select>
      </div>

      {/* Restrictions + correction tip reminder */}
      <p className="text-[10px] text-amber-200/70 italic mt-2 leading-snug">
        ⚠️ {t('regional.restrictions', 'Texte et vocaux uniquement (pas de photos ni vidéos). But non lucratif.')}
      </p>
      <p className="text-[10px] text-emerald-200/80 italic mt-1 leading-snug">
        ✏️ {t('regional.correctionTip', 'Corrige une traduction incorrecte en appuyant longuement dessus ou en préfixant ton message par « correction: ».')}
      </p>
    </div>
  );
};

const GroupChatView = ({ group, currentUserId, onBack, onOpenInfo }) => {
  const { t, language } = useLanguage();
  const isAfricaGroup = !!group?.id?.startsWith('dsia-africa-');
  const isRegionalLangGroup = group?.id === SYSTEM_GROUP_ID_REGIONAL || isAfricaGroup;
  const [regionalLang, setRegionalLang] = useState(() => {
    try { return localStorage.getItem('dsia_regional_lang') || 'djuka'; } catch { return 'djuka'; }
  });
  const [regionalLangs, setRegionalLangs] = useState([]);
  useEffect(() => {
    if (!isRegionalLangGroup) return;
    const req = isAfricaGroup
      ? axios.get('/api/gpt-africa/info', { params: { lang: language, group_id: group.id } })
      : axios.get('/api/regional-lang/info');
    req.then(r => {
        const langs = r.data?.languages || [];
        setRegionalLangs(langs);
        if (isAfricaGroup && langs.length && !langs.some(l => l.id === regionalLang)) setRegionalLangPersist(langs[0].id);
      })
      .catch(() => {});
  }, [isRegionalLangGroup, isAfricaGroup, group?.id]); // eslint-disable-line
  const setRegionalLangPersist = (id) => {
    setRegionalLang(id);
    try { localStorage.setItem('dsia_regional_lang', id); } catch {}
  };

  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [translateTargetMsg, setTranslateTargetMsg] = useState(null);
  const [translatingMsgId, setTranslatingMsgId] = useState(null);

  // Long-press + emoji reactions (parity with DMs)
  const [reactionPickerFor, setReactionPickerFor] = useState(null);
  const longPressTimerRef = useRef(null);
  const QUICK_REACTIONS = ['❤️', '😂', '😮', '😢', '👍', '🔥'];
  const startLongPress = (msg) => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      setReactionPickerFor(msg);
      try { navigator.vibrate?.(40); } catch (_) {}
    }, 350);
  };
  const cancelLongPress = () => {
    if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
  };
  const reactToMessage = async (msg, emoji) => {
    setReactionPickerFor(null);
    try {
      const res = await axios.post(`/api/messages/${msg.id}/react`, {
        emoji, scope: 'group', group_id: group?.id,
      });
      setMessages(prev => prev.map(x => x.id === msg.id ? { ...x, reactions: res.data?.reactions } : x));
    } catch (_) {
      toast.error('Réaction impossible');
    }
  };
  const [translationResult, setTranslationResult] = useState(null);
  const [showCreatePoll, setShowCreatePoll] = useState(false);
  const canCreatePoll = !!(group.is_creator || group.is_admin);
  // Snapshot of the user's last_read_at when entering the group — used to
  // render the "── Nouveaux messages ──" divider above the first unseen msg.
  const [lastReadAtSnapshot, setLastReadAtSnapshot] = useState(null);
  const [lightbox, setLightbox] = useState(null); // {type, src} | null
  const keyboardHeight = useKeyboardHeight();
  const scrollRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const [pendingVoice, setPendingVoice] = useState(null);
  const recordingStartRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`/api/groups/${group.id}/messages`, { params: { lang: language } });
      const fresh = res.data || [];
      setMessages(prev => {
        const wasAtBottom = scrollRef.current
          ? (scrollRef.current.scrollHeight - scrollRef.current.scrollTop - scrollRef.current.clientHeight < 80)
          : true;
        const sameCount = Array.isArray(prev) && prev.length === fresh.length;
        const sameLast = sameCount && prev[prev.length - 1]?.id === fresh[fresh.length - 1]?.id
          && JSON.stringify(prev[prev.length - 1]?.reactions) === JSON.stringify(fresh[fresh.length - 1]?.reactions);
        if (sameLast) return prev;
        // Only auto-scroll when user was at bottom (or new message added).
        if (wasAtBottom || (Array.isArray(prev) && fresh.length > prev.length)) {
          setTimeout(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, 50);
        }
        return fresh;
      });
    } catch (e) {}
  };

  useEffect(() => {
    // Capture last_read_at BEFORE marking the group as read on the server, so
    // we can still display an "unread" divider for messages received while away.
    setLastReadAtSnapshot(group.last_read_at || null);
    load();
    // Mark group as read on the server (a few seconds later so the snapshot
    // we just captured remains valid for the divider).
    const markReadTimer = setTimeout(() => {
      axios.post(`/api/groups/${group.id}/mark-read`).catch(() => {});
    }, 1500);
    // Poll every 2s (matches the DM cadence so groups feel as instant as 1-to-1 chats).
    const i = setInterval(load, 2000);
    return () => {
      clearInterval(i);
      clearTimeout(markReadTimer);
    };
  }, [group.id, language]); // eslint-disable-line

  const send = async () => {
    if (!draft.trim()) return;
    if (draft.length > 10500) {
      toast.error(`Message trop long (${draft.length}/10500 caractères max — équivalent 3 pages Word)`);
      return;
    }
    setSending(true);
    try {
      const body = { content: draft.trim(), type: 'text' };
      if (isRegionalLangGroup) body.regional_lang = regionalLang;
      await axios.post(`/api/groups/${group.id}/messages`, body);
      setDraft('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const uploadMedia = async (file, mediaType, durationSeconds = null) => {
    if (!file) return;
    if (mediaType === 'video' && durationSeconds && durationSeconds > 30) {
      toast.error('Video must be ≤ 30s');
      return;
    }
    const fd = new FormData();
    fd.append('media', file);
    fd.append('media_type', mediaType);
    if (durationSeconds) fd.append('duration_seconds', String(Math.ceil(durationSeconds)));
    setSending(true);
    try {
      await axios.post(`/api/groups/${group.id}/media`, fd);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setSending(false); }
  };

  const handleImagePick = (e) => uploadMedia(e.target.files?.[0], 'image');
  const handleVideoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Probe duration via createObjectURL + video element
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = url;
    await new Promise(res => { video.onloadedmetadata = res; });
    const dur = video.duration;
    URL.revokeObjectURL(url);
    if (dur && dur > 120) {
      toast.error('Vidéo limitée à 2 min (120s)');
      return;
    }
    uploadMedia(file, 'video', dur);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Prefer mp4/m4a for iOS Safari/Capacitor playback compatibility,
      // fall back to webm/opus on Android/Chrome.
      const mimeType =
        typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : (MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm');
      const mr = new MediaRecorder(stream, { mimeType });
      recordedChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const ext = mimeType.includes('mp4') ? 'm4a' : 'webm';
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: mimeType });
        stream.getTracks().forEach(tr => tr.stop());
        const duration = Math.ceil((Date.now() - recordingStartRef.current) / 1000);
        // Preview before sending (WhatsApp-style)
        setPendingVoice({ file, url: URL.createObjectURL(blob), duration });
      };
      recordingStartRef.current = Date.now();
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setRecordingDuration(0);

      // 1 s tick to show duration + auto-stop at 120 s (max voice length)
      recordingTimerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - recordingStartRef.current) / 1000);
        setRecordingDuration(elapsed);
        if (elapsed >= 120) {
          stopRecording();
        }
      }, 250);
    } catch (e) {
      toast.error('Microphone access denied');
    }
  };
  const stopRecording = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    mediaRecorderRef.current?.stop();
    setRecording(false);
    setRecordingDuration(0);
  };

  const deleteMsg = async (msgId) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await axios.delete(`/api/groups/${group.id}/messages/${msgId}`);
      setMessages(prev => prev.filter(m => m.id !== msgId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suppression échouée');
    }
  };

  const handleTranslateVoiceGroup = async (msgId, audioUrl, targetLang) => {
    setTranslatingMsgId(msgId);
    setTranslateTargetMsg(null);
    try {
      const fd = new FormData();
      fd.append('audio_url', audioUrl);
      fd.append('target_lang', targetLang);
      const resp = await axios.post('/api/translate-voice-by-url', fd);
      setTranslationResult({ ...resp.data, message_id: msgId });
    } catch (e) {
      toast.error('Erreur de traduction');
    } finally {
      setTranslatingMsgId(null);
    }
  };


  const downloadMedia = async (mediaUrl, filenameHint = 'media') => {
    try {
      const url = getMediaUrl(mediaUrl);
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      const ext = (mediaUrl.split('.').pop() || '').split('?')[0] || 'bin';
      a.download = `${filenameHint}-${Date.now()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (e) {
      toast.error('Téléchargement échoué');
    }
  };

  const REPORT_USER_I18N = {
    fr: ['Signaler cet utilisateur', 'Motif du signalement ?', 'Utilisateur signalé — merci, un admin va examiner'],
    en: ['Report this user', 'Reason for the report?', 'User reported — thanks, an admin will review'],
    es: ['Denunciar a este usuario', '¿Motivo de la denuncia?', 'Usuario denunciado — gracias, un admin lo revisará'],
    ar: ['الإبلاغ عن هذا المستخدم', 'سبب الإبلاغ؟', 'تم الإبلاغ — شكرًا، سيراجعه مشرف'],
    pt: ['Denunciar este utilizador', 'Motivo da denúncia?', 'Utilizador denunciado — obrigado, um admin vai analisar'],
    de: ['Diesen Nutzer melden', 'Grund der Meldung?', 'Nutzer gemeldet — danke, ein Admin prüft'],
    ru: ['Пожаловаться на пользователя', 'Причина жалобы?', 'Жалоба отправлена — спасибо, админ проверит'],
    zh: ['举报该用户', '举报原因？', '已举报——感谢，管理员将审核'],
    ja: ['このユーザーを報告', '報告理由は？', '報告しました — 管理者が確認します'],
    hi: ['इस उपयोगकर्ता की रिपोर्ट करें', 'रिपोर्ट का कारण?', 'रिपोर्ट भेजी गई — धन्यवाद, एडमिन समीक्षा करेगा'],
    nl: ['Deze gebruiker melden', 'Reden van de melding?', 'Gebruiker gemeld — bedankt, een admin bekijkt het'],
    sw: ['Ripoti mtumiaji huyu', 'Sababu ya ripoti?', 'Mtumiaji ameripotiwa — asante, msimamizi atakagua'],
    ht: ['Rapòte itilizatè sa a', 'Rezon rapò a?', 'Itilizatè rapòte — mèsi, yon admin pral verifye'],
  };
  const reportUser = async (userId) => {
    const L = REPORT_USER_I18N[language] || REPORT_USER_I18N.en;
    const reason = window.prompt(L[1], '');
    if (reason === null) return;
    if (reason.trim().length < 3) { toast.error(L[1]); return; }
    try {
      await axios.post(`/api/groups/${group.id}/members/${userId}/report`, { reason: reason.trim() });
      toast.success(L[2]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const reportMsg = async (msgId) => {
    const reason = window.prompt('Raison du signalement ?', '');
    if (reason === null) return;
    try {
      await axios.post(`/api/groups/${group.id}/messages/${msgId}/report`, { reason });
      toast.success('Message signalé');
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black text-white"
      data-testid="group-chat-view"
    >
      <div
        className="flex items-center gap-3 px-2 py-2 border-b border-white/10 bg-black/95 backdrop-blur-md shrink-0"
        style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}
      >
        <button onClick={onBack} className="p-1 hover:bg-white/10 rounded-full" data-testid="group-chat-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <button onClick={onOpenInfo} className="flex items-center gap-2 flex-1 min-w-0 text-left">
          <div className="w-10 h-10 rounded-full bg-violet-700/30 overflow-hidden flex items-center justify-center shrink-0">
            {group.profile_image_url ? <img src={getMediaUrl(group.profile_image_url)} alt={group.name} className="w-full h-full object-cover" /> : <Users className="w-5 h-5 text-violet-300" />}
          </div>
          <div className="min-w-0">
            <p className="font-semibold truncate">{group.name}</p>
            <p className="text-xs text-gray-400 truncate">{group.theme}</p>
          </div>
        </button>
        <button onClick={onOpenInfo} className="p-2 hover:bg-white/10 rounded-full" data-testid="group-info-btn"><Info className="w-5 h-5" /></button>
      </div>

      {isRegionalLangGroup && (
        <RegionalLangBanner
          regionalLang={regionalLang}
          setRegionalLang={setRegionalLangPersist}
          languages={regionalLangs}
          t={t}
          africaGroupId={isAfricaGroup ? group.id : null}
        />
      )}

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-3 space-y-2"
        style={{
          // Reserve enough space at the bottom for the fixed input bar (~70px)
          // plus the on-screen keyboard so the last message stays visible.
          paddingBottom: keyboardHeight > 0 ? keyboardHeight + 80 : 80,
        }}
      >
        {messages.length === 0 ? (
          <p className="text-center text-sm text-gray-500 italic mt-12">—</p>
        ) : messages.map((m, idx) => {
          const own = m.user_id === currentUserId;
          const prev = messages[idx - 1];
          const showDate = !prev || new Date(m.created_at).toDateString() !== new Date(prev.created_at).toDateString();
          // Unread divider: show ONCE, right above the first message that
          // arrived after the user's last read timestamp (and not own).
          const showUnread = !own
            && lastReadAtSnapshot
            && new Date(m.created_at) > new Date(lastReadAtSnapshot)
            && (!prev || new Date(prev.created_at) <= new Date(lastReadAtSnapshot));
          return (
            <React.Fragment key={m.id}>
              {showDate && (
                <div className="flex justify-center my-2" data-testid="group-date-separator">
                  <span className="text-[11px] font-medium text-white/60 bg-white/10 px-3 py-1 rounded-full">
                    {formatDayLabel(m.created_at)}
                  </span>
                </div>
              )}
              {showUnread && (
                <div className="flex items-center gap-2 my-3" data-testid="group-unread-divider">
                  <div className="flex-1 h-px bg-violet-500/40" />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-violet-300 bg-violet-500/15 px-2.5 py-1 rounded-full">
                    {t('groups.unread_messages', 'Nouveaux messages')}
                  </span>
                  <div className="flex-1 h-px bg-violet-500/40" />
                </div>
              )}
              <div className={`flex ${own ? 'justify-end' : 'justify-start'} group`}>
              {!own && (
                <div className="w-7 h-7 rounded-full overflow-hidden bg-violet-700/30 mr-2 shrink-0">
                  {m.user?.profile_image_url && <img src={getMediaUrl(m.user.profile_image_url)} alt="" className="w-full h-full object-cover" />}
                </div>
              )}
              <div
                className={`max-w-[78%] rounded-2xl px-3 py-2 ${own ? 'bg-violet-600 text-white' : 'bg-white/10 text-white'} select-none`}
                onMouseDown={() => startLongPress(m)}
                onMouseUp={cancelLongPress}
                onMouseLeave={cancelLongPress}
                onTouchStart={() => startLongPress(m)}
                onTouchEnd={cancelLongPress}
                onTouchMove={cancelLongPress}
                data-testid={`group-msg-${m.id}`}
              >
                {!own && <p className="text-[11px] text-violet-300 font-semibold">{m.user?.name || m.user?.username}</p>}
                {(m.media_type === 'poll' || m.type === 'poll') && m.poll_id && (
                  <PollCard
                    pollId={m.poll_id}
                    currentUserId={currentUserId}
                    canDelete={!!(group.is_creator || group.is_admin || m.sender_id === currentUserId)}
                    onDeleted={() => setMessages(prev => prev.filter(x => x.id !== m.id))}
                  />
                )}
                {(m.media_type === 'gift' || m.type === 'gift') && m.gift_id && (
                  <div className="flex flex-col items-center gap-1 py-2 px-3" data-testid={`group-gift-${m.id}`}>
                    <span className="text-5xl drop-shadow-lg animate-bounce" style={{ animationDuration: '2s' }}>
                      {(() => {
                        // Inline lookup — we use a cached catalog (loaded lazily)
                        const cache = window.__giftCatalogCache;
                        if (!cache) {
                          window.__giftCatalogCache = [];
                          axios.get('/api/gifts/catalog').then(r => { window.__giftCatalogCache = r.data?.gifts || []; }).catch(() => {});
                        }
                        const g = (window.__giftCatalogCache || []).find(x => x.id === m.gift_id);
                        return g?.emoji || '🎁';
                      })()}
                    </span>
                    <span className="text-[10px] text-white/80">Cadeau</span>
                  </div>
                )}
                {m.type === 'text' && (
                  <p className="text-sm whitespace-pre-wrap break-words">
                    <MessageContent
                      text={m.content}
                      renderInline={(t) => renderMentions(t, (u) => window.location.href = `/profile/u/${u}`)}
                    />
                  </p>
                )}
                {m.type === 'image' && m.media_url && (
                  <div className="relative group/media">
                    <img
                      src={getMediaUrl(m.media_url)}
                      alt=""
                      className="max-w-full rounded-lg cursor-zoom-in"
                      onClick={(e) => { e.stopPropagation(); setLightbox({ type: 'image', src: getMediaUrl(m.media_url) }); }}
                      data-testid={`group-image-${m.id}`}
                    />
                    <button
                      onClick={() => downloadMedia(m.media_url, 'group-image')}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 opacity-0 group-hover/media:opacity-100 transition-opacity"
                      title="Télécharger"
                      data-testid={`download-img-${m.id}`}
                    >
                      <Download className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                )}
                {m.type === 'video' && m.media_url && (
                  <div className="relative group/media">
                    <video
                      src={getMediaUrl(m.media_url)}
                      playsInline
                      className="max-w-full rounded-lg cursor-zoom-in"
                      onClick={(e) => { e.stopPropagation(); setLightbox({ type: 'video', src: getMediaUrl(m.media_url) }); }}
                      data-testid={`group-video-${m.id}`}
                    />
                    <button
                      onClick={() => downloadMedia(m.media_url, 'group-video')}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 opacity-0 group-hover/media:opacity-100 transition-opacity"
                      title="Télécharger"
                      data-testid={`download-vid-${m.id}`}
                    >
                      <Download className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                )}
                {m.type === 'voice' && m.media_url && (
                  <VoiceMessagePlayer
                    src={getMediaUrl(m.media_url)}
                    own={own}
                    onTranslate={async () => {
                      // Force-dismiss the iOS native keyboard before showing
                      // the language picker — blur() alone is not enough on
                      // Capacitor, we must call the native Keyboard.hide().
                      try { document.activeElement?.blur?.(); } catch (_) {}
                      try {
                        const { Capacitor } = await import('@capacitor/core');
                        if (Capacitor.isNativePlatform()) {
                          const { Keyboard } = await import('@capacitor/keyboard');
                          await Keyboard.hide();
                        }
                      } catch (_) {}
                      setTimeout(() => setTranslateTargetMsg(m), 120);
                    }}
                    translating={translatingMsgId === m.id}
                    testId={`group-voice-${m.id}`}
                  />
                )}
                {/* Existing emoji reactions (read-only summary). Same chip UI as DMs. */}
                {m.reactions && Object.keys(m.reactions).length > 0 && (
                  <div className={`mt-1 flex gap-1 flex-wrap ${own ? 'justify-end' : 'justify-start'}`} data-testid={`group-reactions-${m.id}`}>
                    {Object.entries(m.reactions).map(([em, users]) => {
                      const myReact = Array.isArray(users) && users.includes(currentUserId);
                      const count = Array.isArray(users) ? users.length : 0;
                      if (!count) return null;
                      return (
                        <button
                          key={em}
                          onClick={(e) => { e.stopPropagation(); reactToMessage(m, em); }}
                          className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-0.5 ${myReact ? 'bg-violet-400/40 ring-1 ring-violet-300' : 'bg-white/15 hover:bg-white/25'}`}
                          data-testid={`group-reaction-${m.id}-${em}`}
                        >
                          <span>{em}</span><span className="font-semibold text-[10px]">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {own && (
                <button
                  onClick={() => deleteMsg(m.id)}
                  className={`${isRegionalLangGroup ? 'opacity-70 hover:opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity ml-1 p-1 hover:bg-white/10 rounded-full self-center`}
                  title="Supprimer"
                  data-testid={`delete-group-msg-${m.id}`}
                >
                  <Trash2 className="w-3 h-3 text-red-400" />
                </button>
              )}
              {!own && (
                <button
                  onClick={() => reportMsg(m.id)}
                  className={`${isRegionalLangGroup ? 'opacity-80 hover:opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity ml-1 p-1 hover:bg-white/10 rounded-full self-center`}
                  title="Signaler"
                  data-testid={`report-msg-${m.id}`}
                >
                  <Flag className="w-3 h-3 text-amber-400" />
                </button>
              )}
              {!own && isRegionalLangGroup && !m.is_bot && m.user_id && m.user_id !== 'system' && (
                <button
                  onClick={() => reportUser(m.user_id)}
                  className="opacity-80 hover:opacity-100 transition-opacity ml-0.5 p-1 hover:bg-white/10 rounded-full self-center"
                  title={(REPORT_USER_I18N[language] || REPORT_USER_I18N.en)[0]}
                  data-testid={`report-user-${m.id}`}
                >
                  <UserMinus className="w-3 h-3 text-red-400" />
                </button>
              )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Emoji reaction picker — appears on long-press (parity with DMs).
          v50: in the regional-languages training group, an extra "Correct"
          action lets the user pre-fill the input with `correction: <source> = `
          based on the selected message. */}
      {reactionPickerFor && (
        <div
          className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center"
          onClick={() => setReactionPickerFor(null)}
          data-testid="group-reaction-picker"
        >
          <div className="bg-gray-900 border border-white/15 rounded-2xl px-3 py-3 shadow-2xl flex flex-col gap-2 max-w-[92vw]" onClick={e => e.stopPropagation()}>
            <div className="flex gap-2">
              {QUICK_REACTIONS.map(em => (
                <button
                  key={em}
                  onClick={() => reactToMessage(reactionPickerFor, em)}
                  className="text-2xl hover:scale-125 transition-transform p-1"
                  data-testid={`group-react-${em}`}
                >
                  {em}
                </button>
              ))}
            </div>
            {isRegionalLangGroup && reactionPickerFor?.content && (reactionPickerFor?.type || 'text') === 'text' && (
              <button
                onClick={() => {
                  // Pre-fill the input with `correction: <original source> = `
                  // so the user only has to type the fixed translation.
                  const raw = (reactionPickerFor.content || '').trim();
                  // Strip an existing `correction:` prefix if any, then split at `=`
                  // to grab just the source phrase.
                  const withoutPrefix = raw.replace(/^\s*(?:correction|correctie)\s*[:：]?\s*/i, '');
                  const eqIdx = withoutPrefix.search(/\s*(?:=|\bis\s+gelijk\s+aan\b|\bégale\b|\bequals?\b)\s*/i);
                  const src = eqIdx > 0 ? withoutPrefix.slice(0, eqIdx).trim() : withoutPrefix.trim();
                  setDraft(`correction: ${src} = `);
                  setReactionPickerFor(null);
                }}
                className="mt-1 w-full px-3 py-2 rounded-lg bg-amber-500/20 border border-amber-400/40 text-amber-200 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-amber-500/30"
                data-testid="group-correct-btn"
              >
                ✏️ {t('regional.correct', 'Corriger cette traduction')}
              </button>
            )}
          </div>
        </div>
      )}

      <div
        className="fixed left-0 right-0 flex items-center gap-2 px-3 py-2 border-t border-white/10 bg-black/95 backdrop-blur-md transition-[bottom] duration-150 z-50"
        style={{
          bottom: keyboardHeight > 0 ? keyboardHeight : 0,
          paddingBottom: keyboardHeight > 0 ? '0.5rem' : 'max(0.5rem, env(safe-area-inset-bottom))',
        }}
      >
        {/* Voice preview before sending (WhatsApp-style) */}
        {pendingVoice && !recording && (
          <div className="absolute bottom-full left-3 right-3 mb-2 z-50">
            <VoicePreviewBar
              url={pendingVoice.url}
              onCancel={() => { URL.revokeObjectURL(pendingVoice.url); setPendingVoice(null); }}
              onSend={async () => {
                const pv = pendingVoice;
                await uploadMedia(pv.file, 'voice', pv.duration);
                URL.revokeObjectURL(pv.url);
                setPendingVoice(null);
              }}
            />
          </div>
        )}

        {/* Backdrop to close attach menu */}
        {showAttachMenu && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowAttachMenu(false)}
            data-testid="group-attach-menu-backdrop"
          />
        )}
        {/* Attachment menu (Photo / Vidéo / Voix combinés) */}
        <div className="relative shrink-0">
          <button
            onClick={() => setShowAttachMenu(v => !v)}
            className={`p-2 rounded-full ${showAttachMenu ? 'bg-violet-600 text-white' : 'hover:bg-white/10'}`}
            title="Ajouter un média"
            data-testid="group-attach-menu-btn"
          >
            <Plus className={`w-5 h-5 transition-transform ${showAttachMenu ? 'rotate-45' : ''}`} />
          </button>
          {showAttachMenu && (
            <div
              className="absolute bottom-12 left-0 bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl p-2 flex flex-col gap-1 z-50 min-w-[170px]"
              data-testid="group-attach-menu"
            >
              <button
                onClick={() => { setShowAttachMenu(false); imageInputRef.current?.click(); }}
                className="flex items-center gap-2 px-3 py-2 hover:bg-white/10 rounded-lg text-sm text-left disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="group-attach-image-option"
                disabled={isRegionalLangGroup}
                title={isRegionalLangGroup ? t('regional.noMedia', 'Indisponible dans ce groupe') : ''}
              >
                <ImageIcon className="w-4 h-4 text-violet-300" /> Photo
              </button>
              <button
                onClick={() => { setShowAttachMenu(false); videoInputRef.current?.click(); }}
                className="flex items-center gap-2 px-3 py-2 hover:bg-white/10 rounded-lg text-sm text-left disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="group-attach-video-option"
                disabled={isRegionalLangGroup}
                title={isRegionalLangGroup ? t('regional.noMedia', 'Indisponible dans ce groupe') : ''}
              >
                <Video className="w-4 h-4 text-violet-300" /> Vidéo (≤2 min)
              </button>
              <button
                onClick={() => {
                  setShowAttachMenu(false);
                  if (recording) stopRecording(); else startRecording();
                }}
                className="flex items-center gap-2 px-3 py-2 hover:bg-white/10 rounded-lg text-sm text-left"
                data-testid="group-attach-voice-option"
              >
                {recording
                  ? (<><Square className="w-4 h-4 text-red-400" /> Arrêter</>)
                  : (<><Mic className="w-4 h-4 text-violet-300" /> Vocal</>)}
              </button>
              {canCreatePoll && (
                <button
                  onClick={() => { setShowAttachMenu(false); setShowCreatePoll(true); }}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-white/10 rounded-lg text-sm text-left border-t border-white/10 mt-1 pt-2"
                  data-testid="group-attach-poll-option"
                >
                  <BarChart3 className="w-4 h-4 text-violet-300" /> Sondage
                </button>
              )}
            </div>
          )}
        </div>
        <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} data-testid="group-attach-image" />
        <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={handleVideoPick} data-testid="group-attach-video" />

        {recording && (
          <button
            onClick={stopRecording}
            className="p-2 rounded-full bg-red-600 animate-pulse shrink-0 flex items-center gap-1"
            title="Arrêter l'enregistrement"
            data-testid="group-stop-recording"
          >
            <Square className="w-4 h-4" />
            <span className="text-[11px] font-mono">{recordingDuration}s / 120</span>
          </button>
        )}

        <input
          value={draft}
          onChange={e => setDraft(e.target.value.slice(0, 10500))}
          maxLength={10500}
          onKeyDown={e => e.key === 'Enter' && send()}
          placeholder={t('groups.placeholder_message') || 'Message…'}
          className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-violet-500"
          data-testid="group-message-input"
        />
        {draft.length > 8500 && (
          <span className={`text-[10px] font-mono ${draft.length >= 10500 ? 'text-red-400' : 'text-amber-300'}`} data-testid="group-charcount">
            {draft.length}/10500
          </span>
        )}
        <button onClick={send} disabled={sending} className="p-2 rounded-full bg-violet-600 hover:bg-violet-700 disabled:opacity-50 shrink-0" data-testid="send-group-msg-btn">
          <Send className="w-5 h-5" />
        </button>
      </div>

      <CreatePollModal
        open={showCreatePoll}
        onClose={() => setShowCreatePoll(false)}
        groupId={group.id}
        onCreated={() => { load(); }}
      />

      {/* Translate language picker */}
      {translateTargetMsg && (
        <TranslateLanguagePicker
          onPick={(lang) => handleTranslateVoiceGroup(translateTargetMsg.id, translateTargetMsg.media_url, lang)}
          defaultLang={language || 'fr'}
          onClose={() => setTranslateTargetMsg(null)}
        />
      )}

      {/* Translation result */}
      {translationResult && (
        <TranslateResultModal
          result={translationResult}
          onClose={() => setTranslationResult(null)}
        />
      )}

      {/* Full-screen media viewer for images and videos */}
      <MediaLightbox
        open={!!lightbox}
        type={lightbox?.type}
        src={lightbox?.src}
        onClose={() => setLightbox(null)}
      />
    </div>
  );
};

// ----- Voice message player with progress bar (used in group chat) -----
const VoiceMessagePlayer = ({ src, own, onTranslate, translating, testId }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [blobSrc, setBlobSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const pendingPlayRef = useRef(false);

  // Prefetch the audio into a Blob URL so playback starts instantly on the
  // user's tap. iOS Safari/Capacitor often requires two taps when streaming
  // straight from a URL because it lazily fetches the file. With a blob URL
  // the audio is fully in memory before the user even clicks play.
  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    const controller = new AbortController();
    setLoading(true);
    fetch(src, { signal: controller.signal })
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(blob => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setBlobSrc(blobUrl);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      controller.abort();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [src]);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (!a.paused) { a.pause(); setPlaying(false); return; }
    // If the audio is not yet ready (readyState < HAVE_CURRENT_DATA), mark a
    // pending play — handleCanPlay below will trigger play() automatically
    // once the audio has buffered enough. This eliminates the "two-tap" bug.
    if (a.readyState < 2) {
      pendingPlayRef.current = true;
      try { a.load(); } catch (_) {}
      return;
    }
    try {
      await a.play();
      setPlaying(true);
    } catch (_) {
      pendingPlayRef.current = true;
    }
  };

  const onLoaded = () => {
    const d = audioRef.current?.duration;
    if (d && isFinite(d)) setDuration(d);
  };
  const onTime = () => {
    const t = audioRef.current?.currentTime || 0;
    setCurrent(t);
  };
  const onEnded = () => { setPlaying(false); setCurrent(0); };
  const onCanPlay = async () => {
    if (pendingPlayRef.current && audioRef.current) {
      pendingPlayRef.current = false;
      try {
        await audioRef.current.play();
        setPlaying(true);
      } catch (_) {}
    }
  };
  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * duration;
    setCurrent(a.currentTime);
  };

  const fmt = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };
  const progressPct = duration ? (current / duration) * 100 : 0;

  return (
    <div className="flex flex-col gap-1.5 min-w-[200px] sm:min-w-[220px]" data-testid={testId}>
      <audio
        ref={audioRef}
        src={blobSrc || src}
        preload="auto"
        onLoadedMetadata={onLoaded}
        onTimeUpdate={onTime}
        onEnded={onEnded}
        onCanPlay={onCanPlay}
        playsInline
      />
      <div className="flex items-center gap-2">
        <button
          onClick={toggle}
          className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${own ? 'bg-white/20 hover:bg-white/30' : 'bg-violet-600 hover:bg-violet-700'}`}
          aria-label={playing ? 'Pause' : 'Play'}
          data-testid={`${testId}-toggle`}
        >
          {playing ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
        </button>
        <div
          onClick={seek}
          className={`flex-1 h-1.5 rounded-full overflow-hidden cursor-pointer ${own ? 'bg-white/20' : 'bg-white/10'}`}
        >
          <div
            className={`h-full rounded-full ${own ? 'bg-white' : 'bg-violet-400'}`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className={`text-[10px] font-mono ${own ? 'text-white/80' : 'text-white/60'}`}>
          {fmt(duration ? duration - current : 0)}
        </span>
      </div>
      <button
        type="button"
        onClick={onTranslate}
        disabled={translating}
        className={`self-start inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium disabled:opacity-50 ${
          own ? 'bg-white/15 hover:bg-white/25 text-white' : 'bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300'
        }`}
        data-testid={`${testId}-translate-btn`}
      >
        <Languages className="w-3 h-3" /> {translating ? 'Traduction…' : 'Traduire'}
      </button>
    </div>
  );
};

// ----- Translation language picker -----
const TranslateLanguagePicker = ({ onPick, onClose, defaultLang }) => {
  const LANGS = [
    ['fr', '🇫🇷 Français'], ['en', '🇬🇧 English'], ['es', '🇪🇸 Español'], ['de', '🇩🇪 Deutsch'],
    ['it', '🇮🇹 Italiano'], ['pt', '🇵🇹 Português'], ['ar', '🇸🇦 العربية'], ['zh', '🇨🇳 中文'],
    ['ja', '🇯🇵 日本語'], ['ru', '🇷🇺 Русский'], ['hi', '🇮🇳 हिन्दी'], ['sw', '🇰🇪 Kiswahili'],
    ['ht', '🇭🇹 Kreyòl ayisyen'],
  ];
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="group-translate-picker"
    >
      <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full sm:max-w-md p-4 max-h-[80vh] overflow-y-auto">
        <h3 className="text-base font-semibold mb-3">Traduire vers…</h3>
        <div className="grid grid-cols-2 gap-2">
          {LANGS.map(([code, label]) => (
            <button
              key={code}
              onClick={() => onPick(code)}
              className={`px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                code === defaultLang ? 'bg-violet-600 hover:bg-violet-700 text-white' : 'bg-white/5 hover:bg-white/10 text-white'
              }`}
              data-testid={`group-translate-lang-${code}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ----- Translation result modal -----
const TranslateResultModal = ({ result, onClose }) => {
  const audioSrc = result.audio_base64 ? `data:audio/mp3;base64,${result.audio_base64}` : null;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="group-translate-result"
    >
      <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-md p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">Traduction</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-full" data-testid="group-translate-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Texte original</p>
          <p className="text-sm bg-white/5 p-3 rounded-lg whitespace-pre-wrap">{result.original_text}</p>
        </div>
        <div>
          <p className="text-xs text-cyan-400 uppercase tracking-wide mb-1">Traduction</p>
          <p className="text-sm bg-cyan-500/10 border border-cyan-500/30 p-3 rounded-lg whitespace-pre-wrap">{result.translated_text}</p>
        </div>
        {audioSrc && (
          <audio src={audioSrc} controls className="w-full" data-testid="group-translate-audio" />
        )}
      </div>
    </div>
  );
};

const GroupInfoModal = ({ group: initialGroup, currentUserId, onClose, onLeave }) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [group, setGroup] = useState(initialGroup);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editProfileUrl, setEditProfileUrl] = useState(initialGroup.profile_image_url || '');
  const [editRules, setEditRules] = useState(initialGroup.rules || '');
  const [savingEdit, setSavingEdit] = useState(false);
  const editFileRef = useRef(null);
  const isMember = group.is_member;
  const isCreator = group.is_creator;
  const pending = group.has_pending_request;

  // Re-fetch the group data when the app language changes so a localized
  // (system) group's name/theme/rules instantly reflect the new language.
  useEffect(() => {
    let mounted = true;
    axios.get(`/api/groups/${initialGroup.id}`, { params: { lang: language } })
      .then(r => { if (mounted && r.data) { setGroup(r.data); setEditRules(r.data.rules || ''); } })
      .catch(() => {});
    return () => { mounted = false; };
  }, [language, initialGroup.id]);

  useEffect(() => {
    (async () => {
      if (isMember) {
        try {
          const res = await axios.get(`/api/groups/${group.id}/members`);
          setMembers(res.data || []);
        } catch (e) {}
      }
    })();
  }, [group.id, isMember]);

  const pickEditImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post('/api/groups/upload-image', fd);
      setEditProfileUrl(res.data.url || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload échoué');
    }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await axios.put(`/api/groups/${group.id}`, {
        profile_image_url: editProfileUrl || null,
        rules: editRules,
      });
      toast.success('Groupe mis à jour');
      setEditing(false);
      // Refresh local view
      Object.assign(group, { profile_image_url: editProfileUrl, rules: editRules });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Échec de la mise à jour');
    } finally {
      setSavingEdit(false);
    }
  };

  const requestJoin = async () => {
    setLoading(true);
    try {
      await axios.post(`/api/groups/${group.id}/join`);
      toast.success(t('groups.pending') || 'Demande envoyée');
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const leave = async () => {
    if (!window.confirm(t('groups.leave') || 'Quitter le groupe ?')) return;
    setLoading(true);
    try { await axios.post(`/api/groups/${group.id}/leave`); onLeave(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const deleteGroup = async () => {
    if (!window.confirm(t('groups.delete') || 'Supprimer le groupe ?')) return;
    setLoading(true);
    try { await axios.delete(`/api/groups/${group.id}`); onLeave(); }
    catch (e) { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[55] bg-black/80 flex items-end sm:items-center justify-center"
      data-testid="group-info-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-zinc-900 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md border border-white/10 flex flex-col"
        style={{ maxHeight: '92dvh', paddingTop: 'env(safe-area-inset-top, 0)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
          <h3 className="text-lg font-semibold">{t('groups.info') || 'Informations'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div
          className="flex-1 overflow-y-auto p-4 space-y-4 overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <div className="px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2 text-xs text-emerald-200">
            <Lock className="w-3.5 h-3.5" />
            <span>{t('groups.encryptedStorage', 'Conversation chiffrée au stockage')}</span>
          </div>
          <div className="flex flex-col items-center text-center gap-2">
            <div className="relative w-24 h-24">
              <div className="w-24 h-24 rounded-full bg-violet-700/30 overflow-hidden flex items-center justify-center">
                {(editing ? editProfileUrl : group.profile_image_url)
                  ? <img src={getMediaUrl(editing ? editProfileUrl : group.profile_image_url)} alt={group.name} className="w-full h-full object-cover" />
                  : <Users className="w-12 h-12 text-violet-300" />}
              </div>
              {isCreator && editing && (
                <>
                  <button
                    onClick={() => editFileRef.current?.click()}
                    className="absolute bottom-0 right-0 p-1.5 rounded-full bg-violet-600 hover:bg-violet-700 border-2 border-zinc-900"
                    title="Changer la photo"
                    data-testid="edit-group-photo-btn"
                  >
                    <ImageIcon className="w-3.5 h-3.5 text-white" />
                  </button>
                  <input ref={editFileRef} type="file" accept="image/*" className="hidden" onChange={pickEditImage} />
                </>
              )}
            </div>
            <h2 className="text-xl font-bold">{group.name}</h2>
            <p className="text-sm text-violet-300">{group.theme}</p>
            {group.creator && (
              <p className="text-xs text-gray-400">
                {t('groups.created_by') || 'Créé par'} <span className="text-white">{group.creator.name || group.creator.username}</span>
              </p>
            )}
            {isCreator && !editing && (
              <button
                onClick={() => setEditing(true)}
                className="mt-2 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 rounded-full text-xs font-medium"
                data-testid="edit-group-btn"
              >
                Modifier le groupe
              </button>
            )}
          </div>

          {editing ? (
            <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2" data-testid="group-edit-rules-section">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Règles du groupe</p>
              <textarea
                value={editRules}
                onChange={(e) => setEditRules(e.target.value)}
                rows={4}
                placeholder="Décrivez les règles…"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-500"
                data-testid="edit-rules-input"
              />
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => {
                    setEditing(false);
                    setEditProfileUrl(group.profile_image_url || '');
                    setEditRules(group.rules || '');
                  }}
                  disabled={savingEdit}
                  className="flex-1 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm disabled:opacity-50"
                  data-testid="cancel-edit-group-btn"
                >
                  Annuler
                </button>
                <button
                  onClick={saveEdit}
                  disabled={savingEdit}
                  className="flex-1 px-3 py-2 bg-violet-600 hover:bg-violet-700 rounded-lg text-sm font-medium disabled:opacity-50"
                  data-testid="save-edit-group-btn"
                >
                  {savingEdit ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </div>
          ) : group.rules && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{t('groups.rules') || 'Règles'}</p>
              <p className="text-sm whitespace-pre-wrap">{group.rules}</p>
            </div>
          )}

          {isMember && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">{t('groups.members') || 'Membres'} ({members.length})</p>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {members.map(m => {
                  const isMe = m.id === currentUserId;
                  return (
                    <div key={m.id} className="flex items-center gap-2 p-2 hover:bg-white/5 rounded-lg">
                      <button
                        onClick={() => { onClose(); navigate(`/profile/${m.id}`); }}
                        className="flex items-center gap-2 flex-1 min-w-0 text-left"
                      >
                        <div className="w-8 h-8 rounded-full bg-violet-700/30 overflow-hidden">
                          {m.profile_image_url && <img src={getMediaUrl(m.profile_image_url)} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{m.name || m.username}</p>
                          <p className="text-[10px] text-gray-500">@{m.username}</p>
                        </div>
                        {m.role === 'creator' && <ShieldCheck className="w-4 h-4 text-violet-400" />}
                        {m.role === 'admin' && <ShieldCheck className="w-4 h-4 text-amber-400" />}
                      </button>
                      {/* Owner-only controls: promote/demote */}
                      {isCreator && !isMe && m.role !== 'creator' && (
                        <button
                          onClick={async () => {
                            try {
                              if (m.role === 'admin') {
                                await axios.post(`/api/groups/${group.id}/admins/demote`, { user_id: m.id });
                                toast.success('Rétrogradé');
                              } else {
                                await axios.post(`/api/groups/${group.id}/admins/promote`, { user_id: m.id });
                                toast.success('Promu admin');
                              }
                              const res = await axios.get(`/api/groups/${group.id}/members`);
                              setMembers(res.data || []);
                            } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
                          }}
                          className="p-1.5 hover:bg-white/10 rounded-full"
                          title={m.role === 'admin' ? 'Rétrograder' : 'Promouvoir admin'}
                          data-testid={`promote-${m.id}`}
                        >
                          <Crown className={`w-4 h-4 ${m.role === 'admin' ? 'text-amber-400' : 'text-gray-500'}`} />
                        </button>
                      )}
                      {/* Kick: creator can kick anyone (except self); admin can kick non-admins */}
                      {!isMe && m.role !== 'creator' && (isCreator || (group.is_admin && m.role !== 'admin')) && (
                        <button
                          onClick={async () => {
                            if (!window.confirm('Exclure ce membre ?')) return;
                            try {
                              await axios.delete(`/api/groups/${group.id}/members/${m.id}`);
                              toast.success('Membre exclu');
                              const res = await axios.get(`/api/groups/${group.id}/members`);
                              setMembers(res.data || []);
                            } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
                          }}
                          className="p-1.5 hover:bg-red-500/20 rounded-full"
                          title="Exclure"
                          data-testid={`kick-${m.id}`}
                        >
                          <UserMinus className="w-4 h-4 text-red-400" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="pt-2 space-y-2">
            {!isMember && !pending && (
              <button onClick={requestJoin} disabled={loading} className="w-full py-2 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-50 font-semibold" data-testid="request-join-btn">
                {t('groups.request_join') || 'Demander à rejoindre'}
              </button>
            )}
            {pending && (
              <div className="w-full py-2 rounded-xl bg-amber-500/20 text-amber-300 text-center text-sm font-semibold">
                {t('groups.pending') || 'Demande en attente'}
              </div>
            )}
            {isMember && !isCreator && (
              <>
                <button
                  onClick={async () => {
                    const reason = window.prompt('Raison du signalement ?', '');
                    if (reason === null) return;
                    try {
                      await axios.post(`/api/groups/${group.id}/report`, { reason });
                      toast.success('Groupe signalé');
                    } catch (e) { toast.error('Failed'); }
                  }}
                  className="w-full py-2 rounded-xl bg-white/5 hover:bg-amber-500/20 text-amber-300 font-semibold flex items-center justify-center gap-2"
                  data-testid="report-group-btn"
                >
                  <Flag className="w-4 h-4" /> {t('groups.reportGroup', 'Signaler le groupe')}
                </button>
                <button onClick={leave} disabled={loading} className="w-full py-2 rounded-xl bg-white/5 hover:bg-red-500/20 text-red-400 font-semibold flex items-center justify-center gap-2" data-testid="leave-group-btn">
                  <LogOut className="w-4 h-4" /> {t('groups.leave') || 'Quitter'}
                </button>
              </>
            )}
            {isCreator && (
              <button onClick={deleteGroup} disabled={loading} className="w-full py-2 rounded-xl bg-white/5 hover:bg-red-500/20 text-red-400 font-semibold flex items-center justify-center gap-2" data-testid="delete-group-btn">
                <Trash2 className="w-4 h-4" /> {t('groups.delete') || 'Supprimer'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupsPanel;
